// @ts-nocheck

import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

import Formatter from '../../i18n/formatter';
import { __ } from '../../i18n/l10n';
import theme from '../../styled-components/dark';
import HistoryItemCashoutButtonVariation from '../HistoryItemCashoutButtonVariation';
import RoundedButton from '../RoundedButton';

const inputRangeCss = css`
  .input-range {
    box-sizing: border-box;
    padding: 2em 1.5em 2em 1em;
    position: relative;
    width: 100%;
  }
  .input-range__label {
    display: none;
  }
  .input-range__track--background {
    width: 100%;
    position: absolute;
  }
  .input-range__track {
    background-color: #eee;
    border-radius: 2px;
    cursor: pointer;
    display: block;
    height: 0.1em;
    position: relative;
  }
  .input-range__track--active {
    background: ${({ theme }) => theme.common.colors.secondary};
  }
  .input-range--disabled {
    opacity: 0.5;
  }
  .input-range__slider-container {
    top: 0.15em;
    left: 0;
  }
  .input-range__slider {
    appearance: none;
    background: ${theme.History.betslip.actions.cashoutButtonBackgroundColor};
    border-radius: 100%;
    cursor: pointer;
    display: block;
    height: 1.4em;
    width: 1.4em;
    outline: none;
    position: absolute;
    transform: translate3d(-50%, -50%, 0);
  }
`;

const Wrapper = styled.div`
  ${/* sc-declaration */ inputRangeCss};
`;

const ButtonWrapper = styled.div``;

const StyledSecondaryButton = styled(RoundedButton)`
  box-sizing: border-box;
  width: 100%;
  height: 2.27em;

  ${({ $success, theme }) => {
    if ($success) {
      return css`
        background-color: ${theme.common.colors.positive};
        color: #fff;
      `;
    }
  }};
  ${({ disabled }) => (disabled ? 'opacity: 0.25; pointer-events: none;' : '')}
`;

type PropTypes = {
  cashoutValue: number;
  cashoutStatus: string;
  cashoutAvailable: boolean;
  onCashoutSubmit: (...params: unknown) => unknown;
  cashoutAmount: number;
  showSelector: boolean;
  onShowSelector: (...params: unknown) => unknown;
};

const HistoryItemCashoutButton = ({
  cashoutValue = 0,
  cashoutStatus,
  cashoutAvailable,
  onCashoutSubmit,
  cashoutAmount,
  showSelector,
  onShowSelector,
}: PropTypes) => {
  const onClick = useCallback(() => {
    if (showSelector) {
      onCashoutSubmit();
    } else {
      onShowSelector(true);
    }
  }, [onCashoutSubmit, showSelector, onShowSelector]);
  const disabled = useMemo(
    () => !cashoutAvailable || cashoutAmount === 0 || (cashoutStatus !== null && cashoutStatus !== undefined),
    [cashoutAmount, cashoutAvailable, cashoutStatus],
  );
  const cashoutAmountLabel = useMemo(() => Formatter.formatNumber(+cashoutAmount), [cashoutAmount]);
  const cashoutValueLabel = useMemo(() => Formatter.formatNumber(+cashoutValue), [cashoutValue]);
  const buttonLabel = useMemo(() => {
    if (cashoutStatus === true) {
      return __('Cashout validated');
    }
    if (cashoutStatus === false) {
      return __('Cashout validation in progress');
    }
    if (cashoutStatus !== false && cashoutStatus !== true) {
      if (showSelector) {
        return __('Cashout €%1$s', [cashoutAmountLabel]);
      }
      return __('Cashout €%1$s', [cashoutValueLabel]);
    }
    return '';
  }, [cashoutAmountLabel, cashoutStatus, cashoutValueLabel, showSelector]);

  if (!isNaN(cashoutValue) && cashoutValue > 0) {
    return (
      <Wrapper>
        <ButtonWrapper>
          <HistoryItemCashoutButtonVariation
            value={cashoutValue}
            disabled={cashoutStatus === false || cashoutStatus === true}
          >
            <StyledSecondaryButton
              $success={cashoutStatus === true}
              disabled={disabled}
              onClick={onClick}
              label={buttonLabel}
              color={theme.History.betslip.actions.buttonColor}
              backgroundColor={theme.History.betslip.actions.cashoutButtonBackgroundColor}
            />
          </HistoryItemCashoutButtonVariation>
        </ButtonWrapper>
      </Wrapper>
    );
  }
  return <Wrapper />;
};

export default HistoryItemCashoutButton;
